<template>
  <div class="ob-overlay">
    <div class="ob-card">
      <div class="ob-mascot">🦚</div>

      <!-- Progress dots -->
      <div class="ob-dots" aria-hidden="true">
        <span v-for="s in 4" :key="s" class="ob-dot" :class="{ on: step >= s, now: step === s }"></span>
      </div>

      <!-- Step 1: Language -->
      <div v-if="step === 1" class="ob-step">
        <h2 class="ob-title">{{ pick('Hi! Pick your language', 'नमस्ते! अपनी भाषा चुनें') }}</h2>
        <p class="ob-sub">{{ pick('You can change it anytime', 'आप इसे कभी भी बदल सकते हैं') }}</p>
        <div class="ob-lang-row">
          <button class="ob-big-btn ob-lang-btn" :class="{ picked: lang === 'en' }" @click="chooseLanguage('en')">
            <span class="ob-lang-flag">🇬🇧</span> English
          </button>
          <button class="ob-big-btn ob-lang-btn" :class="{ picked: lang === 'hi' }" @click="chooseLanguage('hi')">
            <span class="ob-lang-flag">🇮🇳</span> हिंदी
          </button>
        </div>
      </div>

      <!-- Step 2: Class -->
      <div v-else-if="step === 2" class="ob-step">
        <h2 class="ob-title">{{ pick('Which class are you in?', 'आप किस कक्षा में हैं?') }}</h2>
        <p class="ob-sub">{{ pick('Questions will match your class level', 'सवाल आपकी कक्षा के हिसाब से होंगे') }}</p>
        <div class="ob-grade-grid">
          <button
            v-for="g in [5, 6, 7, 8, 9, 10, 11, 12]"
            :key="g"
            class="ob-big-btn ob-grade-btn"
            :class="{ picked: grade === g }"
            @click="chooseGrade(g)"
          >
            {{ pick('Class', 'कक्षा') }} {{ g }}
          </button>
        </div>
        <button class="ob-back-btn" @click="step = 1">← {{ pick('Back', 'पीछे') }}</button>
      </div>

      <!-- Step 3: Interests. The site covers a lot; asking up front means Home
           can lead with what this student actually came for instead of showing
           everything to everyone. Nothing is removed — the rest stays one tap
           away, and Settings can change this later. -->
      <div v-else-if="step === 3" class="ob-step">
        <h2 class="ob-title">{{ pick('What do you want to learn?', 'आप क्या सीखना चाहते हैं?') }}</h2>
        <p class="ob-sub">{{ pick('Pick as many as you like — you can change this later', 'जितने चाहें चुनें — बाद में बदल सकते हैं') }}</p>
        <div class="ob-interest-grid">
          <button
            v-for="it in INTERESTS"
            :key="it.id"
            class="ob-big-btn ob-interest-btn"
            :class="{ picked: chosen.includes(it.id) }"
            @click="toggleInterest(it.id)"
          >
            <span class="ob-interest-icon">{{ it.icon }}</span>
            <span class="ob-interest-name">{{ it.name[lang] }}</span>
          </button>
        </div>
        <button class="ob-big-btn ob-go-btn" @click="step = 4">
          {{ chosen.length ? pick('Next', 'आगे') : pick('Show me everything', 'मुझे सब दिखाएँ') }} →
        </button>
        <button class="ob-back-btn" @click="step = 2">← {{ pick('Back', 'पीछे') }}</button>
      </div>

      <!-- Step 4: How it works -->
      <div v-else class="ob-step">
        <h2 class="ob-title">{{ pick('How it works', 'यह कैसे काम करता है') }}</h2>
        <div class="ob-rows">
          <div class="ob-row">
            <span class="ob-row-icon">💎</span>
            <span class="ob-row-text">{{ pick('Earn XP by playing games and quizzes', 'खेल और क्विज़ खेलकर XP कमाएँ') }}</span>
          </div>
          <div class="ob-row">
            <span class="ob-row-icon">🔥</span>
            <span class="ob-row-text">{{ pick('Come back daily to grow your streak', 'रोज़ वापस आएँ और अपनी लगन बढ़ाएँ') }}</span>
          </div>
          <div class="ob-row">
            <span class="ob-row-icon">⚡</span>
            <span class="ob-row-text">{{ pick('Race other players in the Arena', 'अखाड़े में दूसरे खिलाड़ियों से मुकाबला करें') }}</span>
          </div>
        </div>
        <button class="ob-big-btn ob-go-btn" @click="finish">
          {{ pick("Let's go!", 'चलो शुरू करें!') }} 🚀
        </button>
        <button class="ob-back-btn" @click="step = 3">← {{ pick('Back', 'पीछे') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { languageState, setLanguage } from '@/services/translationService';
import { INTERESTS, setInterests, getInterests } from '@/services/game';

export default {
  name: 'OnboardingWizard',
  emits: ['done'],
  data() {
    return {
      languageState,
      step: 1,
      INTERESTS,
      chosen: getInterests(),
      grade: Number(localStorage.getItem('myGrade')) || null
    };
  },
  computed: {
    lang() { return this.languageState.currentLanguage; }
  },
  methods: {
    pick(en, hi) { return this.lang === 'hi' ? hi : en; },
    chooseLanguage(l) {
      setLanguage(l);
      this.step = 2;
    },
    chooseGrade(g) {
      this.grade = g;
      this.step = 3;
    },
    toggleInterest(id) {
      const i = this.chosen.indexOf(id);
      if (i === -1) this.chosen.push(id);
      else this.chosen.splice(i, 1);
    },
    finish() {
      localStorage.setItem('myGrade', String(this.grade || 7));
      // An empty list deliberately means "show everything", so a student who
      // skips the step is never locked out of anything.
      setInterests(this.chosen);
      localStorage.setItem('onboarded', '1');
      this.$emit('done');
    }
  }
};
</script>

<style scoped>
.ob-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: rgba(60, 50, 30, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.ob-card {
  position: relative;
  width: 100%;
  max-width: 460px;
  max-height: calc(100vh - 36px);
  /* dvh tracks the shrinking mobile viewport (URL bar), so the card is never
     taller than what the phone actually shows. Ignored by older browsers. */
  max-height: calc(100dvh - 36px);
  overflow-y: auto;
  background: #FFFFFF;
  border: 2px solid #E8E0D2;
  border-radius: 26px;
  box-shadow: 0 6px 0 #E8E0D2, 0 24px 60px rgba(90, 70, 30, 0.3);
  padding: 26px 24px 24px;
  text-align: center;
}

.ob-mascot {
  font-size: 3.4rem;
  line-height: 1;
  animation: ob-bounce 1.2s ease infinite;
}
@keyframes ob-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.ob-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 14px 0 18px;
}
.ob-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #E8E0D2;
  transition: all 0.25s;
}
.ob-dot.on { background: #58CC02; }
.ob-dot.now { transform: scale(1.35); }

.ob-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #3C3C3C;
  margin-bottom: 6px;
}
.ob-sub {
  font-size: 0.92rem;
  font-weight: 600;
  color: #8E8577;
  margin-bottom: 18px;
}

.ob-big-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 56px;
  padding: 12px 18px;
  background: #FFFFFF;
  border: 2px solid #E8E0D2;
  border-radius: 18px;
  box-shadow: 0 4px 0 #E8E0D2;
  font-family: inherit;
  font-weight: 800;
  font-size: 1.1rem;
  color: #4B4B4B;
  cursor: pointer;
  transition: all 0.12s;
}
.ob-big-btn:hover { border-color: #A8E1FF; }
.ob-big-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #E8E0D2; }
.ob-big-btn.picked {
  background: #DDF4FF;
  border-color: #A8E1FF;
  box-shadow: 0 4px 0 #A8E1FF;
  color: #1899D6;
}

.ob-lang-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ob-lang-btn {
  width: 100%;
  min-height: 66px;
  font-size: 1.3rem;
}
.ob-lang-flag { font-size: 1.5rem; }

.ob-grade-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.ob-grade-btn {
  min-height: 58px;
  font-size: 1rem;
  padding: 10px 8px;
}

.ob-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  text-align: left;
}
.ob-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #FFF9F0;
  border: 2px solid #F5EFE3;
  border-radius: 16px;
  padding: 12px 14px;
  min-height: 56px;
}
.ob-row-icon { font-size: 1.8rem; flex-shrink: 0; }
.ob-row-text {
  font-size: 0.98rem;
  font-weight: 700;
  color: #4B4B4B;
}

.ob-go-btn {
  width: 100%;
  min-height: 60px;
  background: #58CC02;
  border-color: #46A302;
  box-shadow: 0 4px 0 #46A302;
  color: #FFFFFF;
  font-size: 1.25rem;
}
.ob-go-btn:hover { border-color: #46A302; filter: brightness(1.05); }
.ob-go-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #46A302; }
.ob-go-btn.picked { background: #58CC02; color: #FFFFFF; }

.ob-back-btn {
  margin-top: 16px;
  min-height: 44px;
  padding: 8px 18px;
  background: transparent;
  border: none;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.95rem;
  color: #8E8577;
  cursor: pointer;
  border-radius: 12px;
}
.ob-back-btn:hover { background: #FFF4CC; color: #4B4B4B; }

/* ── PHONE (≤480px) ──────────────────────────────────────────────────────
   Covers 375px (iPhone SE / budget Android) up through 480px handsets. */
@media screen and (max-width: 480px) {
  /* 18px each side costs 36px of a 375px screen */
  .ob-overlay { padding: 12px; }
  .ob-card {
    padding: 20px 14px 18px;
    border-radius: 22px;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
  }

  /* Decorative mascot shrinks so step 2's 8 buttons stay above the fold */
  .ob-mascot { font-size: 2.8rem; }
  .ob-dots { margin: 12px 0 14px; }

  .ob-title { font-size: 1.25rem; }
  .ob-sub { font-size: 0.88rem; margin-bottom: 14px; }

  /* 8 class buttons (5–12): three 1fr tracks leave ~95px each, and the
     Hindi label "कक्षा 12" clips. Two roomy columns instead. */
  .ob-grade-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .ob-grade-btn {
    min-height: 54px;
    font-size: 0.98rem;
    padding: 10px 6px;
    line-height: 1.15;
    text-align: center;
    white-space: normal;
  }

  .ob-lang-btn { min-height: 60px; font-size: 1.15rem; }

  .ob-row { gap: 10px; padding: 10px 12px; }
  .ob-row-icon { font-size: 1.5rem; }
  .ob-row-text { font-size: 0.92rem; overflow-wrap: break-word; }

  .ob-go-btn { font-size: 1.12rem; min-height: 56px; }
}

.ob-interest-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.ob-interest-btn {
  display: flex; align-items: center; gap: 9px;
  padding: 12px 12px; min-height: 58px; text-align: left;
  font-size: 0.92rem;
}
.ob-interest-icon { font-size: 1.4rem; line-height: 1; flex-shrink: 0; }
.ob-interest-name { min-width: 0; overflow-wrap: break-word; }
@media screen and (max-width: 480px) {
  .ob-interest-grid { gap: 8px; }
  .ob-interest-btn { padding: 10px 9px; min-height: 54px; font-size: 0.82rem; gap: 7px; }
  .ob-interest-icon { font-size: 1.2rem; }
}
</style>
